import React from 'react';
import { dayOfMonth, formatHM, formatLongDate, isWeekend, weekdayShort } from '../../utils/timesheet';

// What a full day is measured against for the bar under each day's total.
const FULL_DAY_SECONDS = 8 * 3600;

/**
 * The week as a column per day: the day's total and a bar toward a full day,
 * then one card per task worked that day. A card opens its entry (or the day,
 * when the task has several); the day heading opens the day view.
 */
export default function WeekColumns({ days, today, entries, taskById, editable, onOpen, onLog, onDayClick, dayFlags = null }) {
  return (
    <div className="ts-week" role="list">
      {days.map((d) => {
        const dayEntries = entries.filter((e) => e.date === d);
        const seconds = dayEntries.reduce((s, e) => s + e.durationSeconds, 0);
        const byTask = new Map();
        dayEntries.forEach((e) => {
          if (!byTask.has(e.taskId)) byTask.set(e.taskId, []);
          byTask.get(e.taskId).push(e);
        });
        const flag = dayFlags?.get(d);
        const weekend = isWeekend(d);
        const empty = !weekend && seconds === 0 && d < today;
        const classes = [
          'ts-week__day',
          weekend ? 'is-weekend' : '',
          d === today ? 'is-today' : '',
          flag || empty ? 'is-flagged' : '',
        ].filter(Boolean).join(' ');

        return (
          <div key={d} className={classes} role="listitem">
            <button type="button" className="ts-week__head" onClick={() => onDayClick(d)} aria-label={`Open ${formatLongDate(d)}`}>
              <span className="ts-week__dow">{weekdayShort(d)}</span>
              <span className="ts-week__dom">{dayOfMonth(d)}</span>
            </button>
            <div className="ts-week__total">
              <span>{seconds ? formatHM(seconds) : '—'}</span>
              <span className="ts-week__bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, (seconds / FULL_DAY_SECONDS) * 100)}%` }} />
              </span>
              {flag ? (
                <span className="ts-week__note">{flag.count} check{flag.count === 1 ? '' : 's'} flagged</span>
              ) : empty ? (
                <span className="ts-week__note">Nothing logged</span>
              ) : null}
            </div>

            {[...byTask.entries()].map(([taskId, cell]) => {
              const task = taskById.get(taskId);
              const root = task ? taskById.get(task.rootId) : null;
              const taskSeconds = cell.reduce((s, e) => s + e.durationSeconds, 0);
              return (
                <button
                  key={taskId}
                  type="button"
                  className="ts-week__item"
                  style={{ '--ts-chip': task?.color || root?.color || '#75777d' }}
                  onClick={() => onOpen(taskId, d, cell)}
                  title={cell.map((e) => e.description).filter(Boolean).join('\n') || undefined}
                >
                  {root && root.id !== task?.id ? <span className="ts-week__project">{root.name}</span> : null}
                  <span className="ts-week__task">{task?.name || 'Unknown task'}</span>
                  <span className="ts-week__hours">{formatHM(taskSeconds)}</span>
                </button>
              );
            })}

            {editable ? (
              <button type="button" className="ts-week__log" onClick={() => onLog(d)}>
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Log time
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
